import { Component, input, inject, output, signal, OnInit } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Size } from '../../orders/order.model';
import { PromotionService } from '../promotion.service';
import { Promotion } from '../promotion';

@Component({
  selector: 'app-promotion',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './promotion.component.html',
  styleUrl: './promotion.component.css'
})
export class PromotionComponent implements OnInit {
  size = input.required<Size>();
  currentPromotion = input<Promotion | undefined>();
  promotionSaved = output<Promotion>();
  promotionDeleted = output<string>(); // Emit promotion ID when deleted
  
  private fb = inject(FormBuilder);
  private promotionService = inject(PromotionService);
  
  errorMessage = signal<string>('');
  isLoading = signal(false);
  showDeleteConfirmation = signal(false);
  
  // Get today's date string for validation
  readonly todayString = new Date().toISOString().split('T')[0];
  
  promotionForm = this.fb.group({
    discountPrice: [null as number | null, [Validators.required, Validators.min(0)]],
    discountPercentage: [null as number | null, [Validators.required, Validators.min(0), Validators.max(100)]],
    startDate: ['', [Validators.required, this.minDateValidator()]],
    endDate: ['', [Validators.required]]
  }, { 
    validators: this.dateRangeValidator() 
  });

  ngOnInit() {
    const promotion = this.currentPromotion();
    
    if (promotion) {
      // Edit mode - populate with existing promotion data
      const originalPrice = this.size().price;
      const discountPrice = promotion.discountPrice;
      const discountPercentage = this.calculateDiscountPercentage(originalPrice, discountPrice);
      
      this.promotionForm.patchValue({
        discountPrice: promotion.discountPrice,
        discountPercentage: discountPercentage,
        startDate: promotion.startDate,
        endDate: promotion.endDate
      });
    } else {
      // Create mode - start with current size price and 0% discount
      this.promotionForm.patchValue({
        discountPrice: this.size().price,
        discountPercentage: 0,
        startDate: this.todayString,
        endDate: this.todayString
      });
    }

    this.setupFormSynchronization();
  }

  private setupFormSynchronization() {
    this.promotionForm.get('startDate')?.valueChanges.subscribe(() => {
      this.promotionForm.get('endDate')?.updateValueAndValidity();
    });

    this.promotionForm.get('discountPercentage')?.valueChanges.subscribe(percentage => {
      if (percentage !== null && percentage >= 0 && percentage <= 100) {
        const originalPrice = this.size().price;
        const discountPrice = originalPrice * (1 - percentage / 100);
        this.promotionForm.get('discountPrice')?.setValue(parseFloat(discountPrice.toFixed(2)), { emitEvent: false });
      }
    });

    this.promotionForm.get('discountPrice')?.valueChanges.subscribe(discountPrice => {
      if (discountPrice !== null && discountPrice >= 0) {
        const originalPrice = this.size().price;
        const percentage = this.calculateDiscountPercentage(originalPrice, discountPrice);
        if (percentage >= 0 && percentage <= 100) {
          this.promotionForm.get('discountPercentage')?.setValue(percentage, { emitEvent: false });
        }
      }
    });
  }

  private calculateDiscountPercentage(originalPrice: number, discountPrice: number): number {
    if (originalPrice <= 0) return 0;
    const percentage = ((originalPrice - discountPrice) / originalPrice) * 100;
    return Math.max(0, Math.min(100, Math.round(percentage)));
  }

  // Custom validator: start date must be today or later
  private minDateValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      
      return control.value >= this.todayString ? null : { minDate: { actual: control.value, min: this.todayString } };
    };
  }

  // Custom validator: end date must be same or after start date
  private dateRangeValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const startDate = control.get('startDate')?.value;
      const endDate = control.get('endDate')?.value;
      
      if (!startDate || !endDate) return null;
      
      return endDate >= startDate ? null : { dateRange: { startDate, endDate } };
    };
  }

  get isEditMode(): boolean {
    return !!this.currentPromotion();
  }

  get startDateErrors() {
    const control = this.promotionForm.get('startDate');
    if (!control?.errors || !control.touched) return null;
    
    if (control.errors['required']) return 'Start date is required';
    if (control.errors['minDate']) return 'Start date cannot be in the past';
    return null;
  }

  get endDateErrors() {
    const control = this.promotionForm.get('endDate');
    if (!control?.errors || !control.touched) return null;
    
    if (control.errors['required']) return 'End date is required';
    if (this.promotionForm.errors?.['dateRange']) return 'End date must be same or after start date';
    return null;
  }

  onSave() {
    if (this.promotionForm.valid) {
      this.errorMessage.set('');
      this.isLoading.set(true);

      const formValue = this.promotionForm.value;
      const promotion: Promotion = {
        id: this.currentPromotion()?.id,
        size: {
          width: this.size().width,
          height: this.size().height
        },
        discountPrice: formValue.discountPrice!,
        startDate: formValue.startDate!,
        endDate: formValue.endDate!
      };

      const saveOperation = this.isEditMode 
        ? this.promotionService.updatePromotion(promotion)
        : this.promotionService.storePromotion(promotion);

      saveOperation.subscribe({
        next: (id) => {
          console.log(`Promotion ${this.isEditMode ? 'updated' : 'created'} with ID:`, id);
          this.isLoading.set(false);
          
          const savedPromotion: Promotion = { ...promotion, id };
          this.promotionSaved.emit(savedPromotion);
        },
        error: (error) => {
          console.error(`Error ${this.isEditMode ? 'updating' : 'saving'} promotion:`, error);
          this.isLoading.set(false);
          this.errorMessage.set(`Failed to ${this.isEditMode ? 'update' : 'save'} promotion. Please try again.`);
        }
      });
    }
  }

  onDeleteClick() {
    this.showDeleteConfirmation.set(true);
  }

  onDeleteCancel() {
    this.showDeleteConfirmation.set(false);
  }

  onDeleteConfirm() {
    const promotion = this.currentPromotion();
    if (!promotion?.id) return;

    this.isLoading.set(true);
    this.showDeleteConfirmation.set(false);

    this.promotionService.deletePromotion(promotion.id).subscribe({
      next: () => {
        console.log('Promotion deleted successfully');
        this.isLoading.set(false);
        this.promotionDeleted.emit(promotion.id!);
      },
      error: (error) => {
        console.error('Error deleting promotion:', error);
        this.isLoading.set(false);
        this.errorMessage.set('Failed to delete promotion. Please try again.');
      }
    });
  }
}
